import * as THREE from "three";

/**
 * The displacement canvas stores per-UV vertical offsets for the clay.
 * Neutral gray (#808080) = zero displacement.
 *   darker (toward #000000) = pushed INWARD (negative displacement along normal)
 *   lighter (toward #FFFFFF) = pulled OUTWARD (positive displacement along normal)
 */

export type PaintTool = "shape" | "pull" | "smooth";

export interface DispEngine {
  canvas: HTMLCanvasElement;
  ctx: CanvasRenderingContext2D;
  texture: THREE.CanvasTexture;
  width: number;
  height: number;
  /**
   * Paint a stroke segment from `prev` UV to `cur` UV. If `prev` is null it paints
   * a single dot at `cur`. Interpolates so fast strokes stay continuous.
   */
  paintStroke: (
    prev: { u: number; v: number } | null,
    cur: { u: number; v: number },
    radiusPx: number,
    strength: number,
    tool: PaintTool,
  ) => void;
  reset: () => void;
  snapshot: () => ImageData;
  restore: (snap: ImageData) => void;
  dispose: () => void;
}

export function createDispEngine(width: number, height: number): DispEngine {
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  if (!ctx) throw new Error("Could not get 2D context for displacement canvas.");

  ctx.fillStyle = "#808080";
  ctx.fillRect(0, 0, width, height);

  const texture = new THREE.CanvasTexture(canvas);
  texture.wrapS = THREE.RepeatWrapping;
  texture.wrapT = THREE.ClampToEdgeWrapping;
  texture.minFilter = THREE.LinearFilter;
  texture.magFilter = THREE.LinearFilter;
  texture.generateMipmaps = false;
  texture.needsUpdate = true;

  /**
   * Paint one radial gradient dot at canvas coords (cx, cy).
   * color is "0,0,0" for push, "255,255,255" for pull, "128,128,128" for smooth.
   */
  function paintDotAt(cx: number, cy: number, radiusPx: number, alpha: number, color: string) {
    ctx!.globalCompositeOperation = "source-over";
    const grad = ctx!.createRadialGradient(cx, cy, 0, cx, cy, radiusPx);
    grad.addColorStop(0, `rgba(${color},${alpha})`);
    grad.addColorStop(0.6, `rgba(${color},${alpha * 0.35})`);
    grad.addColorStop(1, `rgba(${color},0)`);
    ctx!.fillStyle = grad;
    ctx!.beginPath();
    ctx!.arc(cx, cy, radiusPx, 0, Math.PI * 2);
    ctx!.fill();
  }

  /**
   * Paint a dot, mirroring across the horizontal seam if we're within brush radius of u=0 or u=1
   * so brush strokes wrap smoothly around the cylinder.
   */
  function paintDot(cx: number, cy: number, radiusPx: number, alpha: number, color: string) {
    paintDotAt(cx, cy, radiusPx, alpha, color);
    if (cx < radiusPx) {
      paintDotAt(cx + width, cy, radiusPx, alpha, color);
    } else if (cx > width - radiusPx) {
      paintDotAt(cx - width, cy, radiusPx, alpha, color);
    }
  }

  return {
    canvas,
    ctx,
    texture,
    width,
    height,

    paintStroke(prev, cur, radiusPx, strength, tool) {
      // Determine paint color and clamp alpha per tool.
      let color: string;
      let alpha: number;
      if (tool === "shape") {
        color = "0,0,0";
        alpha = Math.min(0.9, Math.max(0.02, strength));
      } else if (tool === "pull") {
        color = "255,255,255";
        alpha = Math.min(0.9, Math.max(0.02, strength));
      } else {
        // smooth: paint toward neutral gray, gentler.
        color = "128,128,128";
        alpha = Math.min(0.7, Math.max(0.02, strength * 1.1));
      }

      // Convert UVs to canvas pixels. Note: canvas y is flipped vs UV.v.
      const cxCur = cur.u * width;
      const cyCur = (1 - cur.v) * height;

      if (!prev) {
        paintDot(cxCur, cyCur, radiusPx, alpha, color);
        texture.needsUpdate = true;
        return;
      }

      const cxPrev = prev.u * width;
      const cyPrev = (1 - prev.v) * height;

      // Handle the horizontal seam: if the shortest path between prev and cur wraps around
      // (delta u > 0.5), we snap the near copy to the other side so we don't draw across
      // the whole canvas.
      let px = cxPrev;
      const dx = cxCur - px;
      if (Math.abs(dx) > width / 2) {
        px += dx > 0 ? width : -width;
      }

      // Distance in pixels.
      const dxSeg = cxCur - px;
      const dySeg = cyCur - cyPrev;
      const dist = Math.sqrt(dxSeg * dxSeg + dySeg * dySeg);

      // Space stamps at roughly radiusPx / 3 for a continuous groove without over-painting.
      const step = Math.max(1, radiusPx / 3);
      const stamps = Math.max(1, Math.ceil(dist / step));
      // Split the total stroke energy across stamps so speed doesn't over-saturate.
      const perStampAlpha = Math.min(alpha, alpha / Math.max(1, stamps / 2));

      for (let i = 1; i <= stamps; i++) {
        const t = i / stamps;
        const sx = px + dxSeg * t;
        const sy = cyPrev + dySeg * t;
        paintDot(sx, sy, radiusPx, perStampAlpha, color);
      }

      texture.needsUpdate = true;
    },

    reset() {
      ctx!.globalCompositeOperation = "source-over";
      ctx!.fillStyle = "#808080";
      ctx!.fillRect(0, 0, width, height);
      texture.needsUpdate = true;
    },
    snapshot() {
      return ctx!.getImageData(0, 0, width, height);
    },
    restore(snap: ImageData) {
      ctx!.putImageData(snap, 0, 0);
      texture.needsUpdate = true;
    },
    dispose() {
      texture.dispose();
    },
  };
}